import Edit16 from '@carbon/icons-react/es/edit/16';
import Save16 from '@carbon/icons-react/es/save/16';
import { useBoolean } from 'ahooks';
import { Divider, Image, Space } from 'antd';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { ifNotProp } from 'styled-tools';
import { ProjectId } from 'api/models';
import { ReactComponent as Description16 } from 'assets/svgs/description.svg';
import { Button, LinkButton } from 'components/atoms/button';
import {
  Collapse as CollapseAtom,
  PanelHeaderTitle,
} from 'components/atoms/collapse';
import { IconShell } from 'components/atoms/icon-shell';
import { RichTextEditor } from 'components/atoms/rich-text-editor';
import { RTE as RTEAtom } from 'components/atoms/rte';
import { ActionBar } from 'components/molecules/action-bar';
import { SectionPanelKeyHash } from 'components/molecules/tasks/details/types';
import { BodyShort01, Text03 } from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import { queueMacrotask } from 'utils/macrotask';

const RTE = styled(RTEAtom)`
  --rte-max-height: none;
  outline: 2px solid;
  outline-color: transparent;
  outline-offset: -2px;
  ${ifNotProp(
    'readonly',
    css`
      outline-color: var(--primary-color);
      box-shadow: var(--shadow-08);
    `,
  )};
`;

const Collapse = styled(CollapseAtom)`
  && .ant-collapse-item .ant-collapse-header {
    padding-right: 0;
    .ant-collapse-extra {
      margin-right: var(--spacing-02);
    }
  }
`;

const CancelButton = styled(Button)`
  && {
    padding-left: var(--spacing-02);
    padding-right: var(--spacing-02);
  }
`;

interface DescriptionPanelProps {
  projectId: number;
  value?: string;
  onChange: (latest: string) => void;
}

interface DescriptionCtaProps {
  onOk: (event: React.MouseEvent<HTMLElement>) => void;
  onCancel: (event: React.MouseEvent<HTMLElement>) => void;
  disableOk: boolean;
}

const DescriptionCTA: FC<DescriptionCtaProps> = ({
  disableOk,
  onOk,
  onCancel,
}) => {
  return (
    <Space split={<Divider type="vertical" />}>
      <LinkButton
        disabled={disableOk}
        size="small"
        icon={<Save16 />}
        onClick={onOk}
      >
        <FormattedMessage id="save" />
      </LinkButton>
      <CancelButton size="small" type="text" onClick={onCancel}>
        <FormattedMessage id="cancel" />
      </CancelButton>
    </Space>
  );
};

const NoContentMessage = styled.div`
  padding: var(--spacing-02) var(--spacing-05);
  ${Text03};
  ${BodyShort01};
`;

const NoDescription = () => (
  <NoContentMessage>
    <FormattedMessage id="task.description.empty" />
  </NoContentMessage>
);

const InvisibleImage = styled(Image).attrs({ width: 0, height: 0 })`
  visibility: hidden;
  width: 0 !important;
  height: 0 !important;
`;

interface ContainerProps {
  width?: string;
}

const Container = styled.div<ContainerProps>`
  .ant-image {
    display: none;
  }
  width: ${(p) => p.width ?? 'auto'};
`;

/*
 * ImageHandler
 * @description is simple parent wrapper for RTE to handle image preview.
 * Expectation: When user clicks on image in editor during readonly, we need to open image preview.
 * Solution: Attaching a click event listener and when target is an img then open a preview.
 * Caveat: This solution won't work if RTE stop propagating click event to parent.
 * */
export const ImageHandler: FC<{
  width?: string;
  canOpenImage?: boolean;
}> = ({ canOpenImage, children, width }) => {
  const [currentImageSrc, setCurrentImageSrc] = useState<string | undefined>();

  const handleClick = (e) => {
    if (e.target && e.target.tagName === 'IMG') {
      const {
        target: { src },
      } = e;

      if (src) {
        setCurrentImageSrc(src);
      }
    }
  };

  const onVisibleChange = (isVisible: boolean) => {
    if (!isVisible) {
      setCurrentImageSrc('');
    }
  };

  return (
    <Container
      onClick={canOpenImage ? (e) => handleClick(e) : undefined}
      width={width}
    >
      {currentImageSrc ? (
        <InvisibleImage
          preview={{
            src: currentImageSrc,
            visible: Boolean(currentImageSrc),
            onVisibleChange,
          }}
        />
      ) : null}
      {children}
    </Container>
  );
};

export const Description: FC<DescriptionPanelProps> = ({
  projectId,
  value = '',
  onChange,
}) => {
  const [
    readOnly,
    { setTrue: changeToReadOnlyMode, setFalse: changeToEditMode },
  ] = useBoolean(true);

  const [rte, setRte] = useState<RichTextEditor | null>(null);

  const translatedDescriptionPlaceholderText = useTranslation(
    'task.description.placeholder',
  );
  const [html, setHtml] = useState<string>(value);

  useEffect(() => {
    setHtml(value);
  }, [value]);

  const setRteContent = useCallback((_html) => setHtml(_html), []);

  const handleEdit = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    changeToEditMode();
    queueMacrotask(() => rte?.focus());
  };

  const handleSave = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    changeToReadOnlyMode();
    if (html !== value) {
      onChange(html);
    }
  };

  const handleCancel = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    changeToReadOnlyMode();
    setHtml(value);
  };

  return (
    <Collapse
      defaultActiveKey={SectionPanelKeyHash.Description}
      expandIconPosition="right"
      expandIcon={() => null}
    >
      <Collapse.Panel
        id={SectionPanelKeyHash.Description}
        key={SectionPanelKeyHash.Description}
        header={
          <PanelHeaderTitle
            icon={
              <IconShell icon={readOnly ? <Description16 /> : <Edit16 />} />
            }
            title={
              readOnly ? (
                <FormattedMessage id="description" />
              ) : (
                <FormattedMessage id="edit_description" />
              )
            }
          />
        }
        extra={
          readOnly ? (
            <Button
              size="small"
              type="link"
              icon={<Edit16 />}
              onClick={handleEdit}
            >
              <FormattedMessage id="edit" />
            </Button>
          ) : (
            <DescriptionCTA
              disableOk={value === html}
              onOk={handleSave}
              onCancel={handleCancel}
            />
          )
        }
      >
        {readOnly && !html ? (
          <NoDescription />
        ) : (
          <ImageHandler canOpenImage={readOnly}>
            <RTE
              ref={(el) => setRte(el)}
              placeholder={translatedDescriptionPlaceholderText}
              data={html}
              onChange={setRteContent}
              readonly={readOnly}
              projectId={ProjectId(projectId)}
            />
          </ImageHandler>
        )}
        {readOnly ? null : rte ? (
          <ActionBar>
            <ActionBar.Secondary>
              <RichTextEditor.InsertImage
                enabled
                onSelect={(images) => rte.insertImages(images)}
              />
              <RichTextEditor.EmojiPicker
                enabled
                onSelect={(emoji) => rte.addEmoji(emoji)}
              />
            </ActionBar.Secondary>
          </ActionBar>
        ) : null}
      </Collapse.Panel>
    </Collapse>
  );
};
